import {
  MessageOutgoingEvent,
  MessageIncomingEvent,
  ReactionMessagePayload,
  useIM,
  ReactionAddedByOtherEvent,
  ReactionRemovedByOtherEvent,
  HistoryMessageItem,
  HistoryResponse,
  isTextMessage,
  ReactionAddedByYouEvent,
  ReactionRemovedByYouEvent,
  isMessageDeleted,
} from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, Ref } from 'vue'

import { useGuestAuth } from '@/composables/useGuestAuth'

import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { useCurrentUser } from '@/shared/composables/current-user'
import { isGuest } from '@/shared/composables/local-storage'
import { ReactionPayload } from '@/shared/types/store'

let allEmojisWithoutDescription: Emoji[] = []
import('@/shared/utils/emoji-catalog').then((m) => {
  allEmojisWithoutDescription = m.allEmojisWithoutDescription
})

type TargetEventId = HistoryMessageItem['eventId']
export type Author = string
export type Emoji = string
export type ReactionData = { isSetByCurrentUser: boolean; authors: Set<Author> }
export type ReactionsMap = Map<Emoji, ReactionData>
type ReactionEvents = Ref<Map<TargetEventId, ReactionsMap>>
const REACTIONS_TO_SAVE_TO_PREFERENCES = 20

const putReactionAtStartOfArray = (array: Array<Emoji>, reaction: Emoji): Array<Emoji> => {
  return array.sort((x, y) => {
    return x === reaction ? -1 : y === reaction ? 1 : 0
  })
}

export const useReactionsStore = defineStore('reactions', () => {
  const im = useIM()
  const preferencesStore = usePreferencesStore()
  const { setPreferences } = preferencesStore
  const { preferences } = storeToRefs(preferencesStore)
  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()

  const getCurrentAuthor = () => {
    return isGuest() && currentGuest.value
      ? currentGuest.value.pServed
      : currentUser.value?.imLogin || ''
  }

  const isAuthorCurrent = (author: string) => {
    if (author === currentUser.value?.imLogin) return true
    return Boolean(
      isGuest() &&
      currentGuest.value &&
      author === currentGuest.value.pServed,
    )
  }

  const recentlyUsedReactions = computed(() => {
    return preferences.value[PreferenceKey.FAVORITE_REACTIONS] ? [...preferences.value[PreferenceKey.FAVORITE_REACTIONS] as string[]] : []
  })

  const customTextReactions = computed({
    get () {
      return preferences.value[PreferenceKey.CUSTOM_TEXT_REACTIONS] ? [...preferences.value[PreferenceKey.CUSTOM_TEXT_REACTIONS] as string[]] : []
    },
    set (newValue) {
      preferences.value[PreferenceKey.CUSTOM_TEXT_REACTIONS] = newValue
    },
  })

  const favoriteFastReaction = computed({
    get () {
      return preferences.value[PreferenceKey.FAVORITE_FAST_REACTION] ? preferences.value[PreferenceKey.FAVORITE_FAST_REACTION] : '👍'
    },
    set (newValue) {
      preferences.value[PreferenceKey.FAVORITE_FAST_REACTION] = newValue
      setPreferences(PreferenceKey.FAVORITE_FAST_REACTION, newValue)
    },
  })

  const defaultReactions = ['👍', '❤️', '🔥', '😄', '🙁']
  const fullReactionCollection = ref<Emoji[]>(Array.from(new Set([...recentlyUsedReactions.value, ...defaultReactions, ...allEmojisWithoutDescription])))
  const isShortReactionsView = ref(true)
  const reactionEvents: ReactionEvents = ref(new Map())

  const allReactionsToShow = computed(() => {
    if (isShortReactionsView.value) {
      return fullReactionCollection.value.slice(0, 5)
    }
    return fullReactionCollection.value
  })


  function setFavoriteReactions (reaction: Emoji) {
    if (customTextReactions.value.includes(reaction)) return
    if (recentlyUsedReactions.value.includes(reaction)){
      putReactionAtStartOfArray(recentlyUsedReactions.value, reaction)
    } else {
      recentlyUsedReactions.value.unshift(reaction)
    }
    if (recentlyUsedReactions.value.length > REACTIONS_TO_SAVE_TO_PREFERENCES) {
      recentlyUsedReactions.value.splice(-1, REACTIONS_TO_SAVE_TO_PREFERENCES)
    }
    putReactionAtStartOfArray(fullReactionCollection.value, reaction)
    setPreferences(PreferenceKey.FAVORITE_REACTIONS, recentlyUsedReactions.value.slice(0, REACTIONS_TO_SAVE_TO_PREFERENCES))
  }

  async function addCustomTextReaction (reaction: Emoji) {
    const newArray = [...customTextReactions.value]
    newArray.push(reaction)
    customTextReactions.value = newArray
    await setPreferences(PreferenceKey.CUSTOM_TEXT_REACTIONS, customTextReactions.value)
  }

  async function deleteCustomTextReaction (index: number) {
    const newArray = [...customTextReactions.value]
    newArray.splice(index, 1)
    customTextReactions.value = newArray
    await setPreferences(PreferenceKey.CUSTOM_TEXT_REACTIONS, customTextReactions.value)
  }

  function deleteReactionsFromStoreByEventId (eventId: number) {
    reactionEvents.value.delete(eventId)
  }

  async function makeOutgoingReaction (payload: ReactionMessagePayload) {
    const response = await im.setReaction(payload)
    if (!response) return
    // @TODO убрать дальнейшую обработку, т.к. библиотека IM выстреливает соответствующее событие,
    // и обработка происходит в слушателе события
    const author = getCurrentAuthor()
    const targetEvent = reactionEvents.value.get(payload.targetEventId)
    const reactionData: ReactionData = {
      isSetByCurrentUser: true,
      authors: new Set([author]),
    }
    const newReactionMap = new Map().set(payload.reactionType, reactionData)
    if (!targetEvent) {
      reactionEvents.value.set(payload.targetEventId, newReactionMap)
      return
    }
    const targetReactionData = targetEvent.get(payload.reactionType)
    if (targetReactionData) {
      if (!targetReactionData.authors.has(author)) {
        targetReactionData.authors.add(author)
        targetReactionData.isSetByCurrentUser = true
      }
    } else {
      targetEvent.set(payload.reactionType, reactionData)
    }
  }

  async function removeOutgoingReaction (payload: ReactionMessagePayload) {
    const response = await im.removeReaction(payload)
    const targetEvent = reactionEvents.value.get(payload.targetEventId)

    if (!response || !targetEvent) return
    // @TODO убрать дальнейшую обработку, т.к. библиотека IM выстреливает соответствующее событие,
    // и обработка происходит в слушателе события
    const targetReactionData = targetEvent.get(payload.reactionType)

    if (!targetReactionData) return

    const author = getCurrentAuthor()
    targetReactionData.authors.delete(author)
    targetReactionData.isSetByCurrentUser = false

    if (!targetReactionData.authors.size) {
      targetEvent.delete(payload.reactionType)
    }

    if (!targetEvent.size) {
      reactionEvents.value.delete(payload.targetEventId)
    }
  }

  function addIncomingReaction (payload: ReactionPayload) {
    const isByCurrent = isAuthorCurrent(payload.author)
    const targetEvent = reactionEvents.value.get(payload.eventId)
    const reactionData: ReactionData = {
      isSetByCurrentUser: isByCurrent,
      authors: new Set([payload.author]),
    }
    if (!targetEvent) {
      const newReactionMap = new Map().set(payload.emoji, reactionData)
      reactionEvents.value.set(payload.eventId, newReactionMap)
      return
    }
    const targetReactionData = targetEvent.get(payload.emoji)
    if (targetReactionData) {
      if (isByCurrent) {
        targetReactionData.isSetByCurrentUser = true
      }
      targetReactionData.authors.add(payload.author)
    } else {
      targetEvent.set(payload.emoji, reactionData)
    }
  }

  function removeIncomingReaction (payload: ReactionPayload) {
    const targetEvent = reactionEvents.value.get(payload.eventId)
    if (!targetEvent) return
    const targetReactionData = targetEvent.get(payload.emoji)
    if (!targetReactionData) return
    targetReactionData.authors.delete(payload.author)
    if (isAuthorCurrent(payload.author)) {
      targetReactionData.isSetByCurrentUser = false
    }
    if (!targetReactionData.authors.size) {
      targetEvent.delete(payload.emoji)
    }
    if (!targetEvent.size) {
      reactionEvents.value.delete(payload.eventId)
    }
  }

  const setReactionsByHistory = (history: HistoryResponse) => {
    history.events
      .filter(isTextMessage)
      .filter((e) => !isMessageDeleted(e))
      .filter(({ item: { reactions }}: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>) => reactions && Object.keys(reactions).length)
      .forEach(({ eventId, timestamp, item: { reactions = {}}}: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>) => {
        Object.keys(reactions).forEach((pServedExecutor) => {
          reactions[pServedExecutor]?.forEach((emoji: string) => {
            const payload = {
              author: pServedExecutor,
              emoji,
              eventId,
              timestamp,
            }
            addIncomingReaction(payload)
          })

        })
      })
  }

  im.onHistory((response: HistoryResponse) => {
    response.events
      .filter((event: HistoryMessageItem): event is HistoryMessageItem<ReactionAddedByYouEvent | ReactionRemovedByYouEvent> => {
        return event.item.type === 'ReactionAddedEvent' || event.item.type === 'ReactionRemovedEvent'
      })
      .forEach((event: HistoryMessageItem<ReactionAddedByYouEvent | ReactionRemovedByYouEvent>) => {
        const { timestamp, item: { type, reactionType, executor, targetEventId }} = event
        const payload = {
          author: executor,
          emoji: reactionType,
          eventId: targetEventId,
          timestamp,
        }
        if (type === 'ReactionAddedEvent') {
          addIncomingReaction(payload)
        } else if (type === 'ReactionRemovedEvent') {
          removeIncomingReaction(payload)
        }
      })
    setReactionsByHistory(response)
  })

  im.onReactionMessage((event: ReactionAddedByOtherEvent | ReactionRemovedByOtherEvent) => {
    const reactionPayload: ReactionPayload = {
      eventId: event.targetEventId,
      emoji: event.reactionType,
      author: event.executor,
      timestamp: event.timestamp,
    }
    if (event.type === 'ReactionAddedEvent') {
      addIncomingReaction(reactionPayload)
    } else if (event.type === 'ReactionRemovedEvent') {
      removeIncomingReaction(reactionPayload)
    }
  })

  return {
    reactionEvents,
    allReactionsToShow,
    isShortReactionsView,
    customTextReactions,
    favoriteFastReaction,
    setFavoriteReactions,
    deleteReactionsFromStoreByEventId,
    makeOutgoingReaction,
    removeOutgoingReaction,
    addIncomingReaction,
    removeIncomingReaction,
    setReactionsByHistory,
    addCustomTextReaction,
    deleteCustomTextReaction,
  }
})
